import {
  PointerEventHandler,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAtom, useSetAtom } from "jotai";
import {
  PROMPT_DOWNLOAD_SCENARIOS,
  PromptDownloadScenariosType,
} from "../components/DownloadAppPrompt/DownloadAppPrompt";
import useAuth from "./useAuth";
import { useNavigate } from "react-router";
import { ROUTES } from "../utils/constants";
import { FunctionWithVoidReturn } from "../utils/constants/generic-types";
import useCreateGameShareLinkMutation from "../react-query/mutations/use-create-game-share-link";
import {
  DEFAULT_VISIBLE_GAME_INDEX,
  gamesFilterIdAtom,
  GamesFilterOptions,
  gameShareLinksAtomWithStorage,
} from "../jotai/atoms/games";
import { GameMediaSummary } from "@nestcoinco/hyper-api-gateway-api-client";
import { visibleGameIndexAtom } from "../jotai/atoms/games";
import { showSuccessToast, showErrorToastDanger } from "../utils/show-toast";

interface UseGamesPageScrollParams {
  scrollContainerRef: RefObject<HTMLElement>;
  defaultVisibleGameIndex?: number;
}

export function useGamesPageScroll({
  scrollContainerRef,
  defaultVisibleGameIndex,
}: UseGamesPageScrollParams) {
  const [visibleGameIndex, setVisibleGameIndex] = useAtom(visibleGameIndexAtom);
  const [selectedGamesFilterId] = useAtom(gamesFilterIdAtom);
  const lastScrollPositionRef = useRef<number>(0);
  const isProgrammaticScrollRef = useRef<boolean>(false);

  const computeVisibleGameIndex = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const { clientHeight, scrollTop: currentScrollPosition } = scrollContainer;
    if (lastScrollPositionRef.current === currentScrollPosition) {
      return;
    }

    const computedIndex = currentScrollPosition / clientHeight;
    const newGameIndex = defaultVisibleGameIndex
      ? computedIndex - 1
      : computedIndex;
    lastScrollPositionRef.current = currentScrollPosition;
    return newGameIndex;
  }, [defaultVisibleGameIndex, scrollContainerRef]);

  const handleScroll = useCallback(() => {
    const scrollEndEventExists = "onscrollend" in window;
    if (scrollEndEventExists) return;

    const newGameIndex = computeVisibleGameIndex();
    // At the point during scroll where the computed index results in an integer/whole number, it signifies that scrolling has ended.
    // We can always be sure of this because the scrollable container always snaps to such a point when scrolling stops, regardless of the point at which the user actually stopped scrolling.
    // This check is useful for browsers that do not support the scrollend event.
    if (newGameIndex !== undefined && Number.isInteger(newGameIndex)) {
      setVisibleGameIndex(newGameIndex);
    }
  }, [computeVisibleGameIndex, setVisibleGameIndex]);

  const handleScrollEnd = useCallback(() => {
    if (isProgrammaticScrollRef.current) {
      isProgrammaticScrollRef.current = false;
      return;
    }

    const newGameIndex = computeVisibleGameIndex();
    if (newGameIndex !== undefined) {
      setVisibleGameIndex(Math.round(newGameIndex));
    }
  }, [computeVisibleGameIndex, setVisibleGameIndex]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    scrollContainer.addEventListener("scroll", handleScroll);
    scrollContainer.addEventListener("scrollend", handleScrollEnd);

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      scrollContainer.removeEventListener("scrollend", handleScrollEnd);
    };
  }, [scrollContainerRef, handleScroll, handleScrollEnd]);

  // If a default visible game index is provided, such as when a user navigates to a specific game on the Games tab, set it as the current visible game index when the component mounts.
  useEffect(() => {
    if (defaultVisibleGameIndex) setVisibleGameIndex(defaultVisibleGameIndex);
  }, [defaultVisibleGameIndex, setVisibleGameIndex]);

  // Handle situations where the visible game index is out of sync with the scroll position of the container.
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const { scrollTop, clientHeight } = scrollContainer;
    const expectedScrollTop = visibleGameIndex * clientHeight;
    if (scrollTop !== expectedScrollTop) {
      isProgrammaticScrollRef.current = true;
      scrollContainer.scrollTo({
        top: expectedScrollTop,
        behavior: "smooth",
      });
    }
    // Adding selectedGamesFilterId as a dependency ensures that when a user selects a different filter, but the visible game index did not change,
    // e.g. in a situation where the switch happened on the first game in the list (with index 0), the scroll position will still be reset to the top.
  }, [scrollContainerRef, visibleGameIndex, selectedGamesFilterId]);

  return {
    visibleGameIndex,
    setVisibleGameIndex,
  };
}

type PromptUserToDownloadApp = (
  promptDownloadScenario: PromptDownloadScenariosType
) => void;

interface UseGamesPageActionsParams {
  gameSlug?: string;
  userHasFavoriteGames?: boolean;
  openDownloadAppPrompt?: FunctionWithVoidReturn;
  handleFavoriteFilterSelection?: FunctionWithVoidReturn;
}

export function useGamesPageActions({
  userHasFavoriteGames,
  openDownloadAppPrompt,
  handleFavoriteFilterSelection,
}: UseGamesPageActionsParams) {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const [isMuted, setIsMuted] = useState<boolean>(true);
  const setVisibleGameIndex = useSetAtom(visibleGameIndexAtom);
  const [selectedGamesFilterId, setSelectedGamesFilterId] =
    useAtom(gamesFilterIdAtom);
  const [promptDownloadScenario, setPromptDownloadScenario] =
    useState<PromptDownloadScenariosType>(
      isLoggedIn
        ? PROMPT_DOWNLOAD_SCENARIOS.LOGGED_IN.OTHERS
        : PROMPT_DOWNLOAD_SCENARIOS.LOGGED_OUT.OTHERS
    );

  const promptUserToDownloadApp: PromptUserToDownloadApp = (
    downloadScenario
  ) => {
    setPromptDownloadScenario(downloadScenario);
    openDownloadAppPrompt?.();
  };

  const changeGamesFilter = (filterId: GamesFilterOptions) => {
    if (!isLoggedIn) {
      navigate(ROUTES.ROOT.LOG_IN);
      return;
    }
    if (filterId === GamesFilterOptions.FAVORITES && !userHasFavoriteGames) {
      handleFavoriteFilterSelection?.();
    } else {
      setVisibleGameIndex(DEFAULT_VISIBLE_GAME_INDEX);
      setSelectedGamesFilterId(filterId);
    }
  };

  const handleGenreClick = (genreName: string) => {
    const route = isLoggedIn
      ? `${ROUTES.GAMES.GENRES}/${genreName.toLowerCase()}`
      : ROUTES.ROOT.LOG_IN;
    navigate(route);
  };

  const handlePlayTournament = () => {
    const downloadScenario = isLoggedIn
      ? PROMPT_DOWNLOAD_SCENARIOS.LOGGED_IN.PLAY_TOURNAMENT
      : PROMPT_DOWNLOAD_SCENARIOS.LOGGED_OUT.PLAY_TOURNAMENT;
    promptUserToDownloadApp(downloadScenario);
  };

  const handlePlayWithFriends = () => {
    promptUserToDownloadApp(
      isLoggedIn
        ? PROMPT_DOWNLOAD_SCENARIOS.LOGGED_IN.PLAY_WITH_FRIENDS
        : PROMPT_DOWNLOAD_SCENARIOS.LOGGED_OUT.PLAY_WITH_FRIENDS
    );
  };

  const toggleMute = useCallback((muteStatus?: boolean) => {
    setIsMuted((prevState) => muteStatus ?? !prevState);
  }, []);

  return {
    isMuted,
    selectedGamesFilterId,
    promptDownloadScenario,
    promptUserToDownloadApp,
    changeGamesFilter,
    handleGenreClick,
    handlePlayTournament,
    handlePlayWithFriends,
    toggleMute,
  };
}

interface UseShareGameLinkActionParams {
  gameId?: string;
}

export function useShareGameLinkAction({
  gameId,
}: UseShareGameLinkActionParams) {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const {
    mutate: createShareLink,
    data: gameShareLink,
    isPending: isCreateShareLinkPending,
    isSuccess: isCreateShareLinkSuccessful,
    reset: resetCreateShareLinkMutation,
  } = useCreateGameShareLinkMutation();
  const [gameShareLinks, setGameShareLinks] = useAtom(
    gameShareLinksAtomWithStorage
  );

  const handleShare = useCallback(async () => {
    if (!isLoggedIn) {
      navigate(ROUTES.ROOT.LOG_IN);
      return;
    }

    if (!gameShareLinks || !gameId) return;

    const link = gameShareLinks[gameId];
    if (!link?.link) return;

    try {
      await navigator.clipboard.writeText(link.link);
      showSuccessToast("Game link copied");
    } catch {
      showErrorToastDanger("Failed to copy game link");
    }
  }, [isLoggedIn, navigate, gameId, gameShareLinks]);

  const currentLink = useMemo(
    () => (gameShareLinks && gameId ? gameShareLinks[gameId] : undefined),
    [gameId, gameShareLinks]
  );

  useEffect(() => {
    if (
      isLoggedIn &&
      gameId &&
      ((gameShareLinks && !gameShareLinks[gameId]) || !gameShareLinks)
    ) {
      createShareLink(gameId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, isLoggedIn]);

  useEffect(() => {
    if (!isCreateShareLinkSuccessful || !gameId) return;

    setGameShareLinks((prev) => ({
      ...(prev || {}),
      [gameId]: gameShareLink,
    }));
    resetCreateShareLinkMutation();
  }, [
    gameId,
    gameShareLink,
    isCreateShareLinkSuccessful,
    resetCreateShareLinkMutation,
    setGameShareLinks,
  ]);

  return {
    isCreateShareLinkPending,
    gameShareLink: currentLink,
    handleShare,
  };
}

interface UseGameMediaSelectionParams {
  gameId?: string;
  gameMedia?: GameMediaSummary[];
}

export function useGameMediaSelection({
  gameId,
  gameMedia,
}: UseGameMediaSelectionParams) {
  const [selectedGamesMedia, setSelectedGamesMedia] = useState<
    Record<string, number>
  >({});
  const mediaIndexExists = gameId
    ? selectedGamesMedia[gameId] !== undefined
    : false;

  const selectedMediaIndex = useMemo(() => {
    if (!gameMedia || !gameMedia.length) return;

    if (gameId && mediaIndexExists) {
      return selectedGamesMedia[gameId];
    }

    const mediaIndex = Math.floor(Math.random() * gameMedia.length);
    return mediaIndex;
  }, [gameId, gameMedia, mediaIndexExists, selectedGamesMedia]);

  // Update the list of selected game media indices when the computation for the selected game media is done
  useEffect(() => {
    if (!gameId || selectedMediaIndex === undefined) return;

    if (!mediaIndexExists) {
      setSelectedGamesMedia((prev) => ({
        ...prev,
        [gameId]: selectedMediaIndex,
      }));
    }
  }, [gameId, mediaIndexExists, selectedMediaIndex, setSelectedGamesMedia]);

  return selectedMediaIndex;
}

export function useVideoSeek(video: HTMLVideoElement | null) {
  const isDragging = useRef<boolean>(false);
  const seekButtonRef = useRef<HTMLButtonElement | null>(null);

  const seek = useCallback(
    (clientX: number) => {
      const seekButton = seekButtonRef.current;
      if (!video || !seekButton) return;

      const rect = seekButton.getBoundingClientRect();
      let pos = (clientX - rect.left) / rect.width;
      pos = Math.max(0, Math.min(1, pos)); // clamp between 0 and 1
      video.currentTime = pos * video.duration;
    },
    [video]
  );

  const handlePointerDown: PointerEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      if (!video) return;

      // Prevent the default scroll behaviour for touch events
      if (e.pointerType === "touch") {
        e.preventDefault();
      }

      video.pause();
      isDragging.current = true;
      seekButtonRef.current = e.currentTarget;
      seek(e.clientX);

      const handlePointerMove = (moveEvent: PointerEvent) => {
        if (isDragging.current) {
          seek(moveEvent.clientX);
        }
      };

      const handlePointerUp = () => {
        video.play();
        isDragging.current = false;
        seekButtonRef.current = null;
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
      };

      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
    },
    [seek, video]
  );

  return handlePointerDown;
}
